import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../hooks/useAuth";

// Guards the peptides surface (/peptides and the peptides Home component).
// Anyone whose org doesn't carry the peptides product line is bounced back to
// "/", where computeHomeRoute sends them to their correct module.
//
// Patients (order-patient plan) are the exception: they belong on /peptides
// but their per-patient org has NO productLines flag (that's a clinic
// provisioning concept — Order - Patient - Signup sets none). Recognize them
// by the authoritative session plan, the same signal usePeptidePricing /
// withProviderOnlyGuard.js:24 use. Without this bypass a patient would be
// bounced to "/", and — once computeHomeRoute correctly routes order-patient
// back to "peptides" — the two would ping-pong in an infinite redirect loop.
//
// Lives in utils/ rather than inline in Routes.js so the unit test can import
// it without dragging in every peptides page (and their transitive `heic-to`
// parsing pain) — mirrors withProviderOnlyGuard.js.
export function withPeptidesGuard(Component) {
  return function PeptidesGuard(props) {
    const { auth } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      // Patients are identified by plan, not productLines. Allow them through
      // as soon as the session names them, regardless of orgInfo hydration.
      if (auth?.session?.currentOrganizationPlan === "order-patient") return;
      // Wait for orgInfo to hydrate before reading productLines — a partial
      // auth state would otherwise wrongly bounce during boot.
      if (!auth?.orgInfo) return;
      if (auth?.orgInfo?.productLines?.peptides !== true) {
        // replace: true so the bounced /peptides entry doesn't linger in
        // history (back-button would otherwise return to a page that
        // immediately re-bounces). Matches withProviderOnlyGuard.js.
        navigate("/", { replace: true });
      }
    }, [auth?.session, auth?.orgInfo, navigate]);

    // Known-blocked (same conditions as the redirect effect): suppress the
    // one-frame mount of the peptides page — it fires peptide fetches and
    // flashes content before the bounce. Patients and still-hydrating
    // sessions render as before.
    const isPatientPlan = auth?.session?.currentOrganizationPlan === "order-patient";
    if (!isPatientPlan && auth?.orgInfo && auth?.orgInfo?.productLines?.peptides !== true) return null;

    return <Component {...props} />;
  };
}
